import React from 'react';
import type {TFunction} from 'i18next';
import {
    Inventory2Outlined,
    KeyboardArrowLeftRounded,
    NotesOutlined,
    PersonOutlined,
    ShoppingCartOutlined,
} from '@mui/icons-material';
import {routeFunctions, routes} from '../../../core/models/static/routes/app-routes';
import type {AccessLevelPackagesWithAggregates} from '../../../core/services/access-levels';
import {getScopeFrameId} from '../../components/panel-command-search';
import type {PanelEntry} from '../types';

export interface CreateVendorPanelEntriesDeps {
    /** `useTranslation('components', {keyPrefix: 'panel-sections.sidebar.vendor'})` */
    t: TFunction<'components'>;
    /** `useTranslation('components', {keyPrefix: 'panel-sections.command-search.pages'})` */
    tCmd: TFunction<'components'>;
    packages: AccessLevelPackagesWithAggregates;
}

/**
 * Builds the shared panel entry catalog for the vendor-scoped sidebar / command search surfaces.
 */
export const createVendorPanelEntries = ({t, tCmd, packages}: CreateVendorPanelEntriesDeps): PanelEntry[] => [
    {
        id: 'vendor-back-to-vendors',
        title: t(tt => tt.backToMainSidebar),
        sidebarTitle: t(tt => tt.backToMainSidebar),
        icon: <KeyboardArrowLeftRounded/>,
        surfaces: ['sidebar'],
        to: routes.vendors,
    },
    {
        id: 'vendor-account-info',
        title: tCmd(tt => tt.vendorAccountInfo),
        sidebarTitle: t(tt => tt.accountInfo),
        icon: <PersonOutlined/>,
        surfaces: ['sidebar', 'command-search'],
        sidebar: {path: routes.vendor.accountInfo},
        resolveTo: (ctx) => {
            const id = getScopeFrameId(ctx.scope, 'vendor');
            if (!id) return undefined;
            return routeFunctions.vendor.accountInfo(Number(id));
        },
        accessKey: 'base.viewVendorAccountInfo',
        hide: () => !packages.base.viewVendorAccountInfo,
        commandSearch: {providerId: 'navigation-vendor', category: 'pages'},
    },
    {
        id: 'vendor-notes',
        title: tCmd(tt => tt.vendorNotes),
        sidebarTitle: t(tt => tt.notes),
        icon: <NotesOutlined/>,
        surfaces: ['sidebar', 'command-search'],
        sidebar: {path: routes.vendor.notes},
        resolveTo: (ctx) => {
            const id = getScopeFrameId(ctx.scope, 'vendor');
            if (!id) return undefined;
            return routeFunctions.vendor.notes(Number(id));
        },
        accessKey: 'base.viewVendorNotes',
        hide: () => !packages.base.viewVendorNotes,
        commandSearch: {providerId: 'navigation-vendor', category: 'pages'},
    },
    {
        id: 'vendor-products',
        title: tCmd(tt => tt.vendorProducts),
        sidebarTitle: t(tt => tt.products),
        icon: <Inventory2Outlined/>,
        surfaces: ['sidebar', 'command-search'],
        sidebar: {path: routes.vendor.products},
        resolveTo: (ctx) => {
            const id = getScopeFrameId(ctx.scope, 'vendor');
            if (!id) return undefined;
            return routeFunctions.vendor.products(Number(id));
        },
        accessKey: 'base.viewVendorProducts',
        hide: () => !packages.base.viewVendorProducts,
        commandSearch: {providerId: 'navigation-vendor', category: 'pages'},
    },
    {
        id: 'vendor-purchases',
        title: t(tt => tt.purchases),
        sidebarTitle: t(tt => tt.purchases),
        icon: <ShoppingCartOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.vendor.purchases.base},
        hide: () => !(
            packages.purchasing.viewPurchaseOrders ||
            packages.purchasing.viewPurchaseHistory
        ),
        children: [
            {
                id: 'vendor-purchases-orders',
                title: tCmd(tt => tt.vendorPurchaseOrders),
                sidebarTitle: t(tt => tt.purchaseOrders),
                subtitle: t(tt => tt.purchases),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.vendor.purchases.orders.base},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'vendor');
                    if (!id) return undefined;
                    return routeFunctions.vendor.purchases.orders.base(Number(id));
                },
                accessKey: 'purchasing.viewPurchaseOrders',
                hide: () => !packages.purchasing.viewPurchaseOrders,
                commandSearch: {providerId: 'navigation-vendor', category: 'pages'},
            },
            {
                id: 'vendor-purchases-history',
                title: tCmd(tt => tt.vendorPurchaseHistory),
                sidebarTitle: t(tt => tt.purchaseHistory),
                subtitle: t(tt => tt.purchases),
                surfaces: ['sidebar', 'command-search'],
                sidebar: {path: routes.vendor.purchases.history.base},
                resolveTo: (ctx) => {
                    const id = getScopeFrameId(ctx.scope, 'vendor');
                    if (!id) return undefined;
                    return routeFunctions.vendor.purchases.history.base(Number(id));
                },
                accessKey: 'purchasing.viewPurchaseHistory',
                hide: () => !packages.purchasing.viewPurchaseHistory,
                commandSearch: {providerId: 'navigation-vendor', category: 'pages'},
            },
        ],
    },
];

export default createVendorPanelEntries;
